import TickClass from 'lib/TickClass';
import BrowserActivity from 'lib/BrowserActivity';

const defaultConfiguration = {
  kronos: {
    enabled: true,
    tolerance: 30,
    targetingCountKey: 'kronosrefreshcount',
    targetingRefreshKey: 'kronosrefresh',
  },
};

/**
 * Monitors user engagement and refreshes ad slots based on configuration
 */
export default class Kronos extends TickClass {
  _MCP;

  _activity;

  /**
   * @param {MCPInstance} MCPRef
   * @param {Object} [configuration] Initial configuration options
   */
  constructor(MCPRef, configuration = {}) {
    super({ autoStartTicker: false });

    this._MCP = MCPRef;
    this._ready = this._constructorAsync(configuration);
  }

  _constructorAsync(configuration) {
    return this._MCP.ready.then(() => {
      const fromConfigurator =
        this._MCP.Configurator.getConfiguration({ defaults: defaultConfiguration }).kronos;

      this._configuration = {
        ...fromConfigurator,
        ...configuration,
      };

      const { tolerance } = this._configuration;
      this._activity = new BrowserActivity({ global: { tolerance } });

      this.isReady = true;
      this.startTicker();
    });
  }

  /**
   * Fires on an interval to check the state of each slot
   */
  tick() {
    const { enabled = true, targetingRefreshKey, targetingCountKey } = this._configuration;

    if (!enabled || !this._activity.isEngaged) return;

    /* eslint-disable-next-line no-unused-vars */
    const slotsToRefresh = Array.from(this._MCP.slots).reduce((acc, [element, slot]) => {
      if (typeof slot === 'object' && slot.enabled === false) return acc;
      const { meta, inViewPercentage, visibility } = slot;
      const { refresh, refreshThreshold, forceRefresh } = meta;
      let { counter = 0 } = meta;

      const overlayVisible = Array.isArray(this._MCP._overlaySelectors) &&
        this._MCP._overlaySelectors.some(selector => document.querySelector(selector));

      // Increment counter only if it is in view
      if (slot.meta.creativeLoaded && visibility > 0.5 && !overlayVisible) {
        slot.setMetaData('counter', counter += 1);
      }

      if (slot.getMetaData('stale')) return acc;

      if (forceRefresh && visibility > 0.5) {
        acc.push(slot);
        return acc;
      }

      if (refresh && refreshThreshold && counter >= refreshThreshold) {
        const kronosRefreshes = (slot.getMetaData('kronosRefreshes') || 0) + 1;
        slot.setMetaData('kronosRefreshes', kronosRefreshes);
        slot.setTargeting(targetingRefreshKey, 'true');
        slot.setTargeting(targetingCountKey, kronosRefreshes);

        acc.push(slot);
      }
      return acc;
    }, []);

    if (slotsToRefresh.length === 0) return;

    const { defaultPrevented = false, asyncEvents } =
      this.dispatchEvent('beforeKronosRefresh', { slots: slotsToRefresh });

    if (defaultPrevented) return;
    Promise.resolve(asyncEvents).then(() => {
      slotsToRefresh.forEach((slot) => {
        slot.clearTargetingAfterRefresh([targetingRefreshKey, targetingCountKey]);
        slot.setMetaData('counter', 0);
        slot.setMetaData('stale', true);
        slot.removeMetaData('forceRefresh');
      });

      this._MCP.refreshSlots(slotsToRefresh);
    });
  }
}
